import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import StatusBadge from '../StatusBadge/StatusBadge';
import CmdbModal from '../CmdbHardware/CmdbModal';
import verificandoToken from '../../../../FuncoesGerais/VerificarToken';
import { ensureProtocol } from '../../../../config/backend';
import './InventarioDrawer.css';

function formatHistoryDate(d) {
    if (!d) return '—';
    const dt = new Date(d);
    return dt.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
}

function InventarioDrawer({ open, onClose, assetId, onEdit, onDocumentar, onVincular, onDesvincular, onFotosCondicao, onRefresh, refreshTrigger }) {
    const [asset, setAsset] = useState(null);
    const [cmdbOpen, setCmdbOpen] = useState(false);
    const [assignment, setAssignment] = useState(null);
    const [historyList, setHistoryList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchAsset = useCallback(async (codHar) => {
        if (!codHar) return null;
        verificandoToken();
        const token = localStorage.getItem('token');
        const response = await axios.get(ensureProtocol(localStorage.getItem('ipBackend2')) + '/hardware/espec', {
            headers: { Authorization: `${token}` },
            params: { codHar }
        });
        if (response.data && response.data.length > 0) {
            const r = response.data[0];
            return {
                codhar: r.codhar ?? r.CODHAR,
                codeqp: r.codeqp ?? r.CODEQP,
                sereqp: (r.sereqp ?? r.SEREQP ?? '').toString(),
                pateqp: (r.pateqp ?? r.PATEQP ?? '').toString(),
                deseqp: (r.deseqp ?? r.DESEQP ?? '').toString(),
                siteqp: (r.siteqp ?? r.SITEQP ?? '').toString()
            };
        }
        return null;
    }, []);

    const fetchAssignment = useCallback(async (codHar) => {
        if (!codHar) return null;
        verificandoToken();
        const token = localStorage.getItem('token');
        try {
            const response = await axios.get(ensureProtocol(localStorage.getItem('ipBackend2')) + '/vinculacao/hardware', {
                headers: { Authorization: `${token}` },
                params: { codHar }
            });
            if (response.data && response.data.length > 0) {
                const r = response.data[0];
                return {
                    codUsu: r.CODUSU ?? r.codUsu,
                    nomUsu: (r.USRNOMCPL ?? r.usrnomcpl ?? '').toString(),
                    seqVin: r.SEQVIN ?? r.seqVin
                };
            }
        } catch (e) {  }
        return null;
    }, []);

    const fetchHistory = useCallback(async (codHar) => {
        if (!codHar) return [];
        verificandoToken();
        const token = localStorage.getItem('token');
        try {
            const response = await axios.get(ensureProtocol(localStorage.getItem('ipBackend2')) + '/hishardware/hardware', {
                headers: { Authorization: `${token}` },
                params: { codHar }
            });
            const raw = Array.isArray(response.data) ? response.data : [];
            return raw.map((r) => ({
                codhar: r.codhar ?? r.CODHAR,
                deshis: r.deshis ?? r.DESHIS ?? '',
                seqhis: r.seqhis ?? r.SEQHIS,
                dathis: r.dathis ?? r.DATHIS ?? ''
            }));
        } catch (e) {
            return [];
        }
    }, []);

    const load = useCallback(async () => {
        if (!assetId) return;
        setLoading(true);
        setError(null);
        try {
            const [a, ass, hist] = await Promise.all([
                fetchAsset(assetId),
                fetchAssignment(assetId),
                fetchHistory(assetId)
            ]);
            if (!a) {
                setError('Ativo não encontrado.');
                setAsset(null);
                setAssignment(null);
                setHistoryList([]);
            } else {
                setAsset(a);
                setAssignment(ass);
                setHistoryList(hist);
            }
        } catch (e) {
            setError('Erro ao carregar.');
            setAsset(null);
            setAssignment(null);
            setHistoryList([]);
        } finally {
            setLoading(false);
        }
    }, [assetId, fetchAsset, fetchAssignment, fetchHistory]);

    useEffect(() => {
        if (open && assetId) load();
        else {
            setAsset(null);
            setAssignment(null);
            setHistoryList([]);
            setError(null);
        }
    }, [open, assetId, load, refreshTrigger]);

    const hasAssignment = assignment != null && (assignment.nomUsu || assignment.codUsu);
    const userName = hasAssignment ? (assignment.nomUsu || `Usuário ${assignment.codUsu}`) : null;
    let assetTitle = '';
    if (asset) {
        const ser = (asset.sereqp || '').trim();
        const des = (asset.deseqp || '').trim().split('\n')[0].trim();
        assetTitle = des || ser || `Ativo #${asset.codhar}`;
    }
    let label = '';
    if (asset) {
        const ser = asset.sereqp || '';
        const pat = asset.pateqp ? 'Pat. ' + asset.pateqp : '';
        label = (ser + ' ' + pat).trim() || ('#' + asset.codhar);
    }

    if (!open) return null;

    return (
        <>
            <div className="inventario-drawer-backdrop" onClick={onClose} aria-hidden="true" />
            <aside className="inventario-drawer" role="dialog" aria-label="Detalhe do ativo">
                <div className="inventario-drawer__header">
                    <div className="inventario-drawer__header-top">
                        <h2 className="inventario-drawer__title">Detalhe do ativo</h2>
                        <button type="button" className="inventario-drawer__close" onClick={onClose} aria-label="Fechar">
                            <i className="fas fa-times" />
                        </button>
                    </div>
                    {!loading && asset && (
                        <>
                            <div className="inventario-drawer__hero">
                                <div className="inventario-drawer__hero-main">
                                    <span className="inventario-drawer__asset-name">{assetTitle}</span>
                                    <span className="inventario-drawer__patrimony">{asset.pateqp ? `Pat. ${asset.pateqp}` : `#${asset.codhar}`}</span>
                                </div>
                                <div className="inventario-drawer__hero-meta">
                                    <StatusBadge siteqp={asset.siteqp} comVinculo={hasAssignment} />
                                    <div className="inventario-drawer__user">
                                        <span className="inventario-drawer__user-avatar" aria-hidden="true">👤</span>
                                        <span className="inventario-drawer__user-name">{userName || 'Sem vínculo'}</span>
                                    </div>
                                </div>
                            </div>
                            <div className="inventario-drawer__actions">
                                <button type="button" className="inventario-drawer__btn inventario-drawer__btn--primary" onClick={() => onEdit(asset.codhar)}><i className="fas fa-edit" /> Editar</button>
                                <button type="button" className="inventario-drawer__btn inventario-drawer__btn--primary" onClick={() => onDocumentar(asset.codhar, label)}><i className="fas fa-file-alt" /> Documentar</button>
                                <button type="button" className="inventario-drawer__btn inventario-drawer__btn--primary" onClick={() => onFotosCondicao && onFotosCondicao(asset.codhar, label)} title="Fotos da condição do equipamento"><i className="fas fa-camera" /> Fotos</button>
                                {!hasAssignment ? (
                                    <button type="button" className="inventario-drawer__btn inventario-drawer__btn--primary" onClick={() => onVincular(asset.codhar)}><i className="fas fa-link" /> Vincular</button>
                                ) : (
                                    <button type="button" className="inventario-drawer__btn inventario-drawer__btn--secondary" onClick={() => onDesvincular(asset.codhar, assignment)}><i className="fas fa-unlink" /> Desvincular</button>
                                )}
                            </div>
                        </>
                    )}
                </div>
                <div className="inventario-drawer__body">
                    {loading && (
                        <div className="inventario-drawer__loading">
                            <i className="fas fa-spinner fa-spin" /> Carregando...
                        </div>
                    )}
                    {error && !asset && (
                        <div className="inventario-drawer__error">{error}</div>
                    )}
                    {!loading && asset && (
                        <>
                            <section className="inventario-drawer__section">
                                <h3 className="inventario-drawer__section-title">Informações técnicas</h3>
                                <dl className="inventario-drawer__dl">
                                    <dt>Código</dt><dd>{asset.codhar}</dd>
                                    <dt>Série</dt><dd>{asset.sereqp || '—'}</dd>
                                    <dt>Equipamento</dt><dd>{asset.codeqp || '—'}</dd>
                                    <dt>Descrição</dt><dd className="inventario-drawer__desc" style={{ whiteSpace: 'pre-wrap' }}>{asset.deseqp || '—'}</dd>
                                </dl>
                            </section>
                            <section className="inventario-drawer__section">
                                <h3 className="inventario-drawer__section-title">Atribuição atual</h3>
                                {hasAssignment ? (
                                    <div className="inventario-drawer__assignment">
                                        <span className="inventario-drawer__user-avatar" aria-hidden="true">👤</span>
                                        <span className="inventario-drawer__user-name">{userName}</span>
                                    </div>
                                ) : (
                                    <p className="inventario-drawer__no-assignment">Nenhum usuário vinculado a este ativo.</p>
                                )}
                            </section>
                            <section className="inventario-drawer__section">
                                <h3 className="inventario-drawer__section-title">CMDB</h3>
                                <button type="button" className="inventario-drawer__btn inventario-drawer__btn--secondary" onClick={() => setCmdbOpen(true)}>
                                    <i className="fas fa-sitemap" /> Garantia, licenças e QR
                                </button>
                            </section>
                            <section className="inventario-drawer__section inventario-drawer__section--timeline">
                                <h3 className="inventario-drawer__section-title">Histórico</h3>
                                {historyList.length === 0 ? (
                                    <p className="inventario-drawer__empty">Nenhum registro no histórico.</p>
                                ) : (
                                    <ul className="inventario-drawer__timeline">
                                        {historyList.map((item, index) => (
                                            <li key={item.seqhis ?? index} className="inventario-drawer__timeline-item">
                                                <span className="inventario-drawer__timeline-dot" />
                                                <div className="inventario-drawer__timeline-content">
                                                    <time className="inventario-drawer__timeline-date">{formatHistoryDate(item.dathis)}</time>
                                                    <p className="inventario-drawer__timeline-text">{item.deshis || '—'}</p>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </section>
                        </>
                    )}
                </div>
            </aside>
            {asset && (
                <CmdbModal
                    open={cmdbOpen}
                    onClose={() => setCmdbOpen(false)}
                    codhar={asset.codhar}
                    patrimonio={asset.pateqp}
                    descricao={asset.deseqp}
                />
            )}
        </>
    );
}

export default InventarioDrawer;
